import {
    Box,
    MuiIcons,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@leap4/frontend";
import React from "react";
import type { PublisherRosterDto, PublisherRowDto } from "../../../../api/generated/models";
import { DASH, formatMetric, formatPercent, signColor } from "../format";

const { KeyboardArrowRight, OpenInNew, TravelExplore } = MuiIcons;

export type SortKey =
    | "cost" | "impressions" | "cpm" | "rpm" | "edgePer1k" | "conversions"
    | "profit" | "cpa" | "roi" | "roas" | "margin" | "grossRevenue" | "takeRate" | "visitRate" | "conversionRate";

interface IColumn {
    key: string;
    label: string;
    sortKey?: SortKey;
    align?: "left" | "right";
    title?: string;
    render: (r: PublisherRowDto) => React.ReactNode;
}

interface IProps {
    roster: PublisherRosterDto;
    conversionLabel: string;
    rpmLabel: string;
    sortKey: SortKey;
    sortDirection: "asc" | "desc";
    onSort: (key: SortKey) => void;
    expanded: string | null;
    onToggle: (publisher: string) => void;
    onOpen: (publisher: string) => void;
    onExplore: (publisher: string) => void;
}

const signed = (v: number | null | undefined): React.ReactNode => (
    <Box component="span" sx={{ color: signColor(v) }}>
        {formatMetric(v, "currency", true)}
    </Box>
);

const pct = (v: number | null | undefined, digits = 1): React.ReactNode =>
    v === null || v === undefined || !Number.isFinite(v) ? DASH : formatPercent(v, digits);

const buildColumns = (
    roster: PublisherRosterDto,
    conversionLabel: string,
    rpmLabel: string
): IColumn[] => [
    {
        key: "name",
        label: roster.axis.label,
        align: "left",
        render: (r) => (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                <Box
                    sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={r.dimValues[0] || DASH}
                >
                    {r.dimValues[0] || DASH}
                </Box>
            </Box>
        ),
    },
    {
        key: "cost",
        label: "Cost",
        sortKey: "cost",
        align: "right",
        title: "Media spend, and this publisher's share of the campaign",
        render: (r) => (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, alignItems: "baseline" }}>
                <span>{formatMetric(r.cost, "currency", true)}</span>
                <Box component="span" sx={{ fontSize: 10, color: "text.disabled", width: 34, textAlign: "left" }}>
                    {r.costShare > 0 ? formatPercent(r.costShare, 0) : ""}
                </Box>
            </Box>
        ),
    },
    {
        key: "impressions",
        label: "Impressions",
        sortKey: "impressions",
        align: "right",
        render: (r) => formatMetric(r.impressions, "count", true),
    },
    { key: "cpm", label: "CPM", sortKey: "cpm", align: "right", render: (r) => formatMetric(r.cpm, "currency") },
    { key: "rpm", label: rpmLabel, sortKey: "rpm", align: "right", render: (r) => formatMetric(r.rpm, "currency") },
    {
        key: "edgePer1k",
        label: "Edge / 1K",
        sortKey: "edgePer1k",
        align: "right",
        title: "RPM minus CPM — profit per thousand impressions",
        render: (r) => signed(r.edgePer1k),
    },
    {
        key: "conversions",
        label: conversionLabel,
        sortKey: "conversions",
        align: "right",
        render: (r) => formatMetric(r.conversions, "count"),
    },
    { key: "profit", label: "Profit", sortKey: "profit", align: "right", render: (r) => signed(r.profit) },
    { key: "cpa", label: "CPA", sortKey: "cpa", align: "right", render: (r) => formatMetric(r.cpa, "currency") },
    { key: "roi", label: "ROI", sortKey: "roi", align: "right", render: (r) => pct(r.roi, 0) },
    { key: "roas", label: "ROAS", sortKey: "roas", align: "right", render: (r) => formatMetric(r.roas, "multiple") },
    { key: "margin", label: "Margin", sortKey: "margin", align: "right", render: (r) => pct(r.margin, 0) },
    { key: "grossRevenue", label: "Gross Revenue", sortKey: "grossRevenue", align: "right", render: (r) => formatMetric(r.grossRevenue, "currency") },
    { key: "takeRate", label: "Take Rate", sortKey: "takeRate", align: "right", render: (r) => pct(r.takeRate, 0) },
    {
        key: "conversionRate",
        label: "CR",
        sortKey: "conversionRate",
        align: "right",
        title: "Attributed conversions / impressions",
        render: (r) => pct(r.conversionRate),
    },
    {
        key: "visitRate",
        label: "VR",
        sortKey: "visitRate",
        align: "right",
        title: "Visits / impressions",
        render: (r) => pct(r.visitRate),
    },
    {
        key: "dropOff",
        label: "Drop-off",
        align: "right",
        title: "Attributed conversions that never verified",
        render: (r) => {
            const v = r.verification.dropOff;
            const heavy = v !== null && v >= 0.5;
            return (
                <Box component="span" sx={{ color: heavy ? "error.main" : "text.primary", fontWeight: heavy ? 600 : 400 }}>
                    {pct(v, 0)}
                </Box>
            );
        },
    },
];

/** Profit per day, so a row's shape is visible without opening it. */
const DailyStrip = ({ daily }: { daily: PublisherRowDto["daily"] }): JSX.Element => {
    const values = daily.map((d) => (d.profit !== null && Number.isFinite(d.profit) ? d.profit : 0));
    const max = Math.max(1, ...values.map(Math.abs));
    return (
        <Box sx={{ display: "flex", gap: "1px", alignItems: "center", height: 26 }}>
            {daily.map((d, i) => {
                const v = values[i];
                const h = Math.max(1, (Math.abs(v) / max) * 11);
                return (
                    <Box
                        key={d.date}
                        title={`${d.date}: ${formatMetric(d.profit, "currency", true)}`}
                        sx={{ width: 5, height: 26, display: "flex", flexDirection: "column", justifyContent: "center" }}
                    >
                        <Box sx={{ height: 13, display: "flex", alignItems: "flex-end" }}>
                            {v >= 0 && <Box sx={{ width: "100%", height: h, bgcolor: "success.main" }} />}
                        </Box>
                        <Box sx={{ height: 13, display: "flex", alignItems: "flex-start" }}>
                            {v < 0 && <Box sx={{ width: "100%", height: h, bgcolor: "error.main" }} />}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

/** Only the figures a single day actually has; the rest of the row stays blank. */
const dailyCell = (key: string, d: PublisherRowDto["daily"][number]): React.ReactNode => {
    if (key === "name") return d.date;
    if (key === "cost") return formatMetric(d.cost, "currency", true);
    if (key === "impressions") return formatMetric(d.impressions, "count", true);
    if (key === "conversions") return formatMetric(d.conversions, "count");
    if (key === "profit") return signed(d.profit);
    return null;
};

const RosterTable = ({
    roster,
    conversionLabel,
    rpmLabel,
    sortKey,
    sortDirection,
    onSort,
    expanded,
    onToggle,
    onOpen,
    onExplore,
}: IProps): JSX.Element => {
    const columns = React.useMemo(
        () => buildColumns(roster, conversionLabel, rpmLabel),
        [roster, conversionLabel, rpmLabel]
    );

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: "68vh" }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 34, p: 0 }} />
                        {columns.map((c) => (
                            <TableCell
                                key={c.key}
                                align={c.align ?? "right"}
                                sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                            >
                                <Tooltip title={c.title ?? ""} arrow disableHoverListener={!c.title}>
                                    {c.sortKey ? (
                                        <Box
                                            component="span"
                                            onClick={() => onSort(c.sortKey as SortKey)}
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.25,
                                                cursor: "pointer",
                                                userSelect: "none",
                                                "&:hover": { color: "primary.main" },
                                            }}
                                        >
                                            {c.label}
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: 9,
                                                    lineHeight: 1,
                                                    visibility: sortKey === c.sortKey ? "visible" : "hidden",
                                                }}
                                            >
                                                {sortDirection === "asc" ? "▲" : "▼"}
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box component="span" sx={{ cursor: c.title ? "help" : "inherit" }}>
                                            {c.label}
                                        </Box>
                                    )}
                                </Tooltip>
                            </TableCell>
                        ))}
                        <TableCell sx={{ width: 90, fontWeight: 600 }}>Daily</TableCell>
                        <TableCell sx={{ width: 70, p: 0 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{ bgcolor: "action.hover", "& td": { fontWeight: 600 } }}>
                        <TableCell sx={{ p: 0 }} />
                        {columns.map((c, i) => (
                            <TableCell key={c.key} align={c.align ?? "right"} sx={{ whiteSpace: "nowrap" }}>
                                {i === 0 ? (
                                    <>
                                        TOTAL{" "}
                                        <Box
                                            component="span"
                                            sx={{ fontWeight: 400, fontSize: 11, color: "text.secondary" }}
                                        >
                                            · {roster.rows.length}{" "}
                                            {roster.axis.label.toLowerCase()}
                                            {roster.rows.length === 1 ? "" : "s"}
                                        </Box>
                                    </>
                                ) : (
                                    c.render(roster.totals)
                                )}
                            </TableCell>
                        ))}
                        <TableCell colSpan={2} />
                    </TableRow>

                    {roster.rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length + 3} align="center" sx={{ py: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No delivery in this range.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}

                    {roster.rows.map((r) => {
                        const name = r.dimValues[0] || "";
                        const isOpen = expanded === name;
                        return (
                            <React.Fragment key={name}>
                                <TableRow
                                    hover
                                    onClick={() => onToggle(name)}
                                    sx={{ cursor: "pointer", ...(isOpen ? { bgcolor: "action.selected" } : {}) }}
                                >
                                    <TableCell sx={{ p: 0, textAlign: "center" }}>
                                        <KeyboardArrowRight
                                            sx={{
                                                fontSize: 16,
                                                color: "text.disabled",
                                                transition: "transform 120ms",
                                                transform: isOpen ? "rotate(90deg)" : "none",
                                            }}
                                        />
                                    </TableCell>
                                    {columns.map((c) => (
                                        <TableCell
                                            key={c.key}
                                            align={c.align ?? "right"}
                                            sx={{
                                                whiteSpace: "nowrap",
                                                fontVariantNumeric: c.align === "left" ? "normal" : "tabular-nums",
                                            }}
                                        >
                                            {c.render(r)}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{ p: 0.5 }}>
                                        <DailyStrip daily={r.daily} />
                                    </TableCell>
                                    <TableCell sx={{ p: 0, whiteSpace: "nowrap", textAlign: "center" }}>
                                        <Tooltip title={`Open ${name}'s dashboard`} arrow>
                                            <Box
                                                component="span"
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    onOpen(name);
                                                }}
                                                sx={{
                                                    display: "inline-flex",
                                                    p: 0.5,
                                                    color: "text.disabled",
                                                    "&:hover": { color: "primary.main" },
                                                }}
                                            >
                                                <OpenInNew sx={{ fontSize: 15 }} />
                                            </Box>
                                        </Tooltip>
                                        <Tooltip title={`Analyse ${name} in Explorer`} arrow>
                                            <Box
                                                component="span"
                                                onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    onExplore(name);
                                                }}
                                                sx={{
                                                    display: "inline-flex",
                                                    p: 0.5,
                                                    color: "text.disabled",
                                                    "&:hover": { color: "primary.main" },
                                                }}
                                            >
                                                <TravelExplore sx={{ fontSize: 15 }} />
                                            </Box>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>

                                {isOpen &&
                                    r.daily.map((d) => (
                                        <TableRow key={`${name}-${d.date}`} sx={{ bgcolor: "action.hover" }}>
                                            <TableCell sx={{ p: 0 }} />
                                            {/* One cell per column, so this row can never drift out of step with the header. */}
                                            {columns.map((c) => (
                                                <TableCell
                                                    key={c.key}
                                                    align={c.align ?? "right"}
                                                    sx={{
                                                        fontSize: 11,
                                                        whiteSpace: "nowrap",
                                                        ...(c.key === "name" ? { pl: 4 } : {}),
                                                        fontVariantNumeric:
                                                            c.align === "left" ? "normal" : "tabular-nums",
                                                    }}
                                                >
                                                    {dailyCell(c.key, d)}
                                                </TableCell>
                                            ))}
                                            <TableCell colSpan={2} />
                                        </TableRow>
                                    ))}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default RosterTable;
